import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Divider,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  Visibility as ViewColumnsIcon,
  Tune as FilterIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Upload as ImportIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { FilterCriteria } from '../../types/company';
import { ApiService } from '../../services/api';

interface FilterOption {
  value: string;
  label: string;
}

interface ColumnOption {
  id: string;
  label: string;
  visible: boolean;
}

interface CompactFilterPanelProps {
  columns: ColumnOption[];
  onColumnToggle: (columnId: string, visible: boolean) => void;
  onFiltersApply: (filters: FilterCriteria[]) => void;
  onClearFilters: () => void;
  onEdit?: () => void;
  onImport?: () => void;
  onDownload?: () => void;
  onAddNew: () => void;
  filterType: 'people' | 'company' | 'industry_tag' | 'country' | 'state' | 'log';
  showFilters?: boolean;
}

const CompactFilterPanel: React.FC<CompactFilterPanelProps> = ({
  columns,
  onColumnToggle,
  onFiltersApply,
  onClearFilters,
  onEdit,
  onImport,
  onDownload,
  onAddNew,
  filterType,
  showFilters = true,
}) => {
  const [viewColumnsOpen, setViewColumnsOpen] = useState(false);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [industryTags, setIndustryTags] = useState<string[]>([]);
  // All industry tag filters (p_tag, industry_tag) 
  // use the same data from industryTags state - optimized to use single API call for both people and company filters
  const [allIndustryTagNames, setAllIndustryTagNames] = useState<string[]>([]);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [verificationStatuses, setVerificationStatuses] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [currentFilter, setCurrentFilter] = useState<FilterCriteria>({
    field: '',
    operator: 'like',
    value: '',
    logic: 'AND',
  });

  const [recentQueries, setRecentQueries] = useState<{ date: string; filters: FilterCriteria[] }[]>([]);

  // Load history on mount or when modal opens
  useEffect(() => {
    if (filterType === 'company') {
      const fetchHistory = async () => {
        try {
          const res = await ApiService.getRecentQueries();
          if (res && res.success && res.queries) {
            const formatted = res.queries.map(q => {
              let dateStr = '';
              try {
                const dateObj = new Date(q.date);
                dateStr = dateObj.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
              } catch (dateErr) {
                dateStr = String(q.date);
              }
              return {
                date: dateStr,
                filters: q.filters
              };
            });
            setRecentQueries(formatted);
          }
        } catch (e) {
          console.error('Failed to load recent filters from database:', e);
        }
      };
      fetchHistory();
    }
  }, [filterType, criteriaOpen]);

  const saveFiltersToHistory = async (appliedFilters: FilterCriteria[]) => {
    if (filterType !== 'company' || appliedFilters.length === 0) return;
    try {
      const res = await ApiService.saveRecentQuery(appliedFilters);
      if (res && res.success) {
        const historyRes = await ApiService.getRecentQueries();
        if (historyRes && historyRes.success && historyRes.queries) {
          const formatted = historyRes.queries.map(q => {
            let dateStr = '';
            try {
              const dateObj = new Date(q.date);
              dateStr = dateObj.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });
            } catch (dateErr) {
              dateStr = String(q.date);
            }
            return {
              date: dateStr,
              filters: q.filters
            };
          });
          setRecentQueries(formatted);
        }
      }
    } catch (e) {
      console.error('Failed to save filter history to database:', e);
    }
  };

  const handleSelectRecentQuery = (index: number) => {
    const selected = recentQueries[index];
    if (selected && selected.filters) {
      setFilters(selected.filters);
      // Auto apply immediately and close modal
      onFiltersApply(selected.filters);
      setCriteriaOpen(false);
      
      // Update history to bring selected query to the top
      saveFiltersToHistory(selected.filters);
    }
  };


  const peopleFilterOptions: FilterOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'in_mailer', label: 'In Mailer' },
    { value: 'unsubscribe', label: 'Unsubscribed' },
    { value: 'news_thc', label: 'News THC' },
    { value: 'news_cbd', label: 'News CBD' },
    { value: 'news_inv', label: 'News INV' },
    { value: 'news_med', label: 'News MED' },
    { value: 'bounce', label: 'Bounce' },
    { value: 'type_', label: 'Type' },
    { value: 'b2b_or_b2c', label: 'B2B/B2C' },
    { value: 'p_tag', label: 'Personal Tag' },
    { value: 'industry_tag', label: 'Industry Tag' },
    { value: 'verification_status', label: 'Verification Status' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'email', label: 'Email' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'company_type', label: 'Company Type' },
    { value: 'license', label: 'License' },
    { value: 'date_added', label: 'Date Added' },
    { value: 'date_updated', label: 'Date Updated' },
    { value: 'last_emailed', label: 'Last Emailed' },
    { value: 'last_clicked', label: 'Last Clicked' },
    { value: 'last_opened', label: 'Last Opened' },
    { value: 'Description', label: 'Description' },
    { value: 'notes', label: 'Notes' },
  ];

  const companyFilterOptions: FilterOption[] = [
    { value: 'name', label: 'Company Name' },
    { value: 'website', label: 'Website' },
    { value: 'type_', label: 'Type' },
    { value: 'industry_tag', label: 'Industry Tag' },
    { value: 'state_usa', label: 'State' },
    { value: 'country', label: 'Country' },
    { value: 'deliverable', label: 'Deliverable' },
    { value: 'risky', label: 'Risky' },
    { value: 'unknown', label: 'Unknown' },
    { value: 'undeliverable', label: 'Undeliverable' },
    { value: 'not_verified', label: 'Not Verified' },
    { value: 'in_mailer', label: 'In Mailer' },
    { value: 'unsubscribe', label: 'Unsubscribe' },
    { value: 'bounce', label: 'Bounce' },
    { value: 'last_ran_snov', label: 'Snov Last Ran' },
    { value: 'last_ran_gmo', label: 'GMO Last Ran' },
    { value: 'last_ran_reoon', label: 'Reoon Last Ran' },
  ];

  const industryTagFilterOptions: FilterOption[] = [
    { value: 'name', label: 'Name' },
    { value: 'status', label: 'Status' },
    { value: 'type_', label: 'Type' },
    { value: 'observations', label: 'Observations' },
  ];

  const countryFilterOptions: FilterOption[] = [
    { value: 'name', label: 'Country Name' },
    { value: 'abbreviation', label: 'Abbreviation' },
  ];

  const stateFilterOptions: FilterOption[] = [
    { value: 'id_pais', label: 'Country ID' },
    { value: 'name', label: 'State Name' },
    { value: 'abbreviation', label: 'Abbreviation' },
  ];

  const logFilterOptions: FilterOption[] = [
    { value: 'username', label: 'Username' },
    { value: 'table_name', label: 'Table' },
    { value: 'operation', label: 'Operation' },
    { value: 'timestamp', label: 'Timestamp' },
  ];

  // Get operators based on selected field
  const getOperatorOptions = (field: string): { value: FilterCriteria['operator']; label: string }[] => {
    const operators: { value: FilterCriteria['operator']; label: string }[] = [];
    
    // Active filter - special labels only
    if (field === 'active') {
      return [
        { value: 'is_not_null' as const, label: 'Active' },
        { value: 'is_null' as const, label: 'Inactive' },
      ];
    }
    
    // Date fields - special date operators
    if (['date_added', 'date_updated', 'last_emailed', 'last_clicked', 'last_opened', 'timestamp', 'last_ran_snov', 'last_ran_gmo', 'last_ran_reoon'].includes(field)) {
      operators.push(
        { value: 'date_exact' as const, label: 'Exact Date' },
        { value: 'date_not_exact' as const, label: 'Not Exact Date' },
        { value: 'date_from' as const, label: 'From Date' },
        { value: 'date_to' as const, label: 'To Date' },
        { value: 'date_between' as const, label: 'Between Dates' },
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    // Log table fields
    else if (field === 'username' && filterType === 'log') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
    }
    else if (field === 'table_name' && filterType === 'log') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
    }
    else if (field === 'operation' && filterType === 'log') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
    }
    // Boolean/null fields - only Has Value and Is Empty (no other operators)
    else if (['deliverable', 'risky', 'unknown', 'undeliverable', 'not_verified', 'in_mailer', 'unsubscribe', 'bounce', 'news_thc', 'news_cbd', 'news_inv', 'news_med'].includes(field)) {
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Empty' }
      );
    }
    // Text search fields (name, website, first_name, last_name, email, company_name, company_type, license)
    else if (['name', 'website', 'first_name', 'last_name', 'email', 'company_name', 'company_type', 'license', 'observations', 'abbreviation'].includes(field)) {
      operators.push({ value: 'like' as const, label: 'Contains' });
      operators.push({ value: 'not_like' as const, label: 'Does Not Contain' });
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    // Dropdown fields for industry_tag table
    else if (field === 'name' && filterType === 'industry_tag') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
    }
    else if (field === 'status' && filterType === 'industry_tag') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
    }
    else if (field === 'type_' && filterType === 'industry_tag') {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    // Dropdown fields (type, industry_tag, state_usa, country, b2b_or_b2c, p_tag)
    else if (['type_', 'industry_tag', 'state_usa', 'country', 'b2b_or_b2c', 'p_tag'].includes(field)) {
      operators.push({ value: 'equals' as const, label: 'Equals' });
      operators.push({ value: 'not_equals' as const, label: 'Does Not Equal' });
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    // Description and notes fields - only Has Value and Is Null
    else if (['Description', 'notes'].includes(field)) {
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    // Default for any other field
    else {
      operators.push({ value: 'like' as const, label: 'Contains' });
      operators.push({ value: 'not_like' as const, label: 'Does Not Contain' });
      operators.push(
        { value: 'is_not_null' as const, label: 'Has Value' },
        { value: 'is_null' as const, label: 'Is Null' }
      );
    }
    
    return operators;
  };

  const operatorOptions = getOperatorOptions(currentFilter.field);

  const filterOptions = filterType === 'people' ? peopleFilterOptions : filterType === 'industry_tag' ? industryTagFilterOptions : filterType === 'country' ? countryFilterOptions : filterType === 'state' ? stateFilterOptions : filterType === 'log' ? logFilterOptions : companyFilterOptions;

  // Fetch industry tags when component mounts
  useEffect(() => {
    const fetchIndustryTags = async () => {
      try {
        const response = filterType === 'people' 
          ? await ApiService.getPeopleIndustryTags()
          : await ApiService.getCompanyIndustryTags();
        setIndustryTags(response.industryTags);
      } catch (error) {
        console.error('Error fetching industry tags:', error);
        // Fallback to empty array if API fails
        setIndustryTags([]);
      }
    };

    // Removed separate fetch functions - all use industryTags from single API call

    // Removed separate fetch functions for company industry tag options - all use industryTags from single API call

    if (filterType === 'company') {
      // Single API call - industryTags will be reused for all filter dropdowns
      // (industry_tag, industry_tag_two, industry_tag_three, industry_tag_four)
      fetchIndustryTags();

      const fetchStatesAndCountries = async () => {
        try {
          const statesRes = await ApiService.getStates();
          if (statesRes && statesRes.data) {
            setStates(statesRes.data.map((s: any) => s.name).filter(Boolean));
          }
          const countriesRes = await ApiService.getCountries();
          if (countriesRes && countriesRes.data) {
            setCountries(countriesRes.data.map((c: any) => c.name).filter(Boolean));
          }
        } catch (error) {
          console.error('Error fetching states or countries for filters:', error);
        }
      };
      fetchStatesAndCountries();
    } else if (filterType === 'people') {
      // Single API call - industryTags will be reused for all filter dropdowns
      // (p_tag, industry_tag)
      fetchIndustryTags();
      // Fetch verification statuses for verification_status filter
      if (verificationStatuses.length === 0) {
        const fetchVerificationStatuses = async () => {
          try {
            const response = await ApiService.getVerificationStatuses();
            setVerificationStatuses(response.verificationStatuses || []);
          } catch (error) {
            console.error('Error fetching verification statuses:', error);
            setVerificationStatuses([]);
          }
        };
        fetchVerificationStatuses();
      }
    } else if (filterType === 'industry_tag' && allIndustryTagNames.length === 0) {
      // Fetch all industry tag names for the name filter dropdown (only if not already fetched)
      const fetchAllIndustryTagNames = async () => {
        try {
          const response = await ApiService.getIndustryTags(null, 1000); // Get up to 1000 tags
          const names = response.data.map((tag: any) => tag.name);
          setAllIndustryTagNames(names);
        } catch (error) {
          console.error('Error fetching all industry tag names:', error);
          setAllIndustryTagNames([]);
        }
      };
      fetchAllIndustryTagNames();
    } else if (filterType === 'log' && usernames.length === 0) {
      // Fetch active user emails for username filter dropdown
      const fetchUsernames = async () => {
        try {
          const response = await ApiService.getUsers(true); // Pass true to indicate it's for filter options
          // Filter only active users (status === 1) and extract emails
          const activeEmails = response.users
            .filter((user: any) => {
              return user.status === 1 && user.email;
            })
            .map((user: any) => user.email);
          setUsernames(activeEmails);
        } catch (error) {
          console.error('Error fetching usernames:', error);
          setUsernames([]);
        }
      };
      fetchUsernames();
    }
    // Note: country filter uses text input, not dropdown, so no need to fetch all country names
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const handleFieldChange = (field: string) => {
    const operators = getOperatorOptions(field);
    setCurrentFilter({
      ...currentFilter,
      field,
      operator: operators[0]?.value || 'like',
      value: '', // Reset value when field changes
    });
  };

  const handleAddFilter = () => {
    let finalFilter = { ...currentFilter };
    
    // Website contains / does not contain empty value interceptor
    if (finalFilter.field === 'website') {
      const isValueEmpty = !finalFilter.value || (typeof finalFilter.value === 'string' && finalFilter.value.trim() === '');
      if (isValueEmpty) {
        if (finalFilter.operator === 'like') {
          // Convert "Website Contains [blank]" to "Website Has Value"
          finalFilter.operator = 'is_not_null';
          finalFilter.value = '';
        } else if (finalFilter.operator === 'not_like') {
          // Convert "Website Does Not Contain [blank]" to "Website Is Null"
          finalFilter.operator = 'is_null';
          finalFilter.value = '';
        }
      }
    }

    // For null/not null operators, value is not required
    const requiresValue = !['is_null', 'is_not_null'].includes(finalFilter.operator);
    
    // For date_between, check both from and to values
    const hasValidValue = requiresValue ? (
      finalFilter.operator === 'date_between' 
        ? (finalFilter.value?.from && finalFilter.value?.to)
        : finalFilter.value
    ) : true;
    
    if (finalFilter.field && (!requiresValue || hasValidValue)) {
      setFilters([...filters, { ...finalFilter }]);
      setCurrentFilter({
        field: '',
        operator: 'like',
        value: '',
        logic: 'AND',
      });
    }
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleRemoveFilterInline = (index: number) => {
    const updated = filters.filter((_, i) => i !== index);
    setFilters(updated);
    if (updated.length > 0) {
      saveFiltersToHistory(updated);
    }
    onFiltersApply(updated);
  };

  const isAddFilterDisabled = () => {
    if (!currentFilter.field) return true;
    
    // Website field is a special case: when operator is 'like' or 'not_like', we allow empty value.
    if (currentFilter.field === 'website' && ['like', 'not_like'].includes(currentFilter.operator)) {
      return false;
    }

    const requiresValue = !['is_null', 'is_not_null'].includes(currentFilter.operator);
    if (!requiresValue) return false;

    if (currentFilter.operator === 'date_between') {
      return !currentFilter.value?.from || !currentFilter.value?.to;
    }

    return !currentFilter.value;
  };

  const handleApplyFilters = () => {
    let currentFiltersToApply = [...filters];
    
    // Auto-add the current filter if it's filled out but the user forgot to click "Add Filter"
    if (!isAddFilterDisabled()) {
      let finalFilter = { ...currentFilter };
      
      // Re-apply the Website contains / does not contain empty value interceptor
      if (finalFilter.field === 'website') {
        const isValueEmpty = !finalFilter.value || (typeof finalFilter.value === 'string' && finalFilter.value.trim() === '');
        if (isValueEmpty) {
          if (finalFilter.operator === 'like') {
            finalFilter.operator = 'is_not_null';
            finalFilter.value = '';
          } else if (finalFilter.operator === 'not_like') {
            finalFilter.operator = 'is_null';
            finalFilter.value = '';
          }
        }
      }
      
      currentFiltersToApply.push(finalFilter);
      setFilters(currentFiltersToApply);
      setCurrentFilter({
        field: '',
        operator: 'like',
        value: '',
        logic: 'AND',
      });
    }

    saveFiltersToHistory(currentFiltersToApply);
    onFiltersApply(currentFiltersToApply);
    setCriteriaOpen(false);
  };

  const handleClearAllFilters = () => {
    setFilters([]);
    onClearFilters();
    setCriteriaOpen(false);
  };

  const renderViewColumnsDialog = () => (
    <Dialog open={viewColumnsOpen} onClose={() => setViewColumnsOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>View Columns</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select which columns to display in the table
        </Typography>
        {columns.map((column) => (
          <FormControlLabel
            key={column.id}
            control={
              <Checkbox
                checked={column.visible}
                onChange={(e) => onColumnToggle(column.id, e.target.checked)}
              />
            }
            label={column.label}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewColumnsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const renderCriteriaDialog = () => (
    <Dialog open={criteriaOpen} onClose={() => setCriteriaOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Filter Criteria</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
            {filterType === 'company' && recentQueries.length > 0 && (
              <Box sx={{ mb: 3, mt: 1 }}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="recent-queries-label">Recent Queries / Load Past Search</InputLabel>
                  <Select
                    labelId="recent-queries-label"
                    id="recent-queries-select"
                    value=""
                    label="Recent Queries / Load Past Search"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== '') {
                        handleSelectRecentQuery(Number(val));
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select a recent query to load...</em>
                    </MenuItem>
                    {recentQueries.map((item, idx) => {
                      const filterStrings = item.filters.map(f => {
                        const fieldLabel = companyFilterOptions.find(opt => opt.value === f.field)?.label || f.field;
                        let valStr = '';
                        if (f.operator === 'is_null') valStr = 'Is Empty';
                        else if (f.operator === 'is_not_null') valStr = 'Has Value';
                        else if (f.operator === 'date_between' && f.value) valStr = `${f.value.from} to ${f.value.to}`;
                        else valStr = typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value);
                        return `${fieldLabel} (${f.operator}) ${valStr || ''}`;
                      }).join(', ');
                      return (
                        <MenuItem key={idx} value={idx}>
                          <Typography variant="body2" sx={{ maxWidth: '100%' }} noWrap>
                            [{item.date}] {filterStrings}
                          </Typography>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select add filter and then apply. For multiple filters, choose AND or OR logic to combine them.
            </Typography>
        
        {/* Current Filters */}
        {filters.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Applied Filters:
            </Typography>
            {filters.map((filter, index) => {
              const fieldLabel = filterOptions.find(opt => opt.value === filter.field)?.label || filter.field;
              const currentOperatorOptions = getOperatorOptions(filter.field);
              const operatorLabel = currentOperatorOptions.find(opt => opt.value === filter.operator)?.label || filter.operator;
              let valueLabel = '';
              
              if (['is_null', 'is_not_null'].includes(filter.operator)) {
                valueLabel = '';
              } else if (filter.operator === 'date_between' && filter.value) {
                valueLabel = ` "${filter.value.from}" to "${filter.value.to}"`;
              } else {
                valueLabel = ` "${filter.value}"`;
              }
              
              return (
                <Chip
                  key={index}
                  label={`${index > 0 ? `(${filter.logic}) ` : ''}${fieldLabel} ${operatorLabel}${valueLabel}`}
                  onDelete={() => handleRemoveFilter(index)}
                  sx={{ m: 0.5 }}
                />
              );
            })}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Add New Filter */}
        <Typography variant="subtitle2" gutterBottom>
          Add New Filter:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Field</InputLabel>
            <Select
              value={currentFilter.field}
              label="Field"
              onChange={(e) => handleFieldChange(e.target.value)}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={currentFilter.operator}
              label="Operator"
              onChange={(e) => setCurrentFilter({ ...currentFilter, operator: e.target.value as FilterCriteria['operator'] })}
            >
              {operatorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Value input - conditional based on field and operator */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {!['is_null', 'is_not_null'].includes(currentFilter.operator) && (
              // State filters - Text search for name and abbreviation, number for id_pais
              filterType === 'state' && currentFilter.field === 'id_pais' ? (
                <TextField
                  label="Value"
                  type="number"
                  value={currentFilter.value}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  sx={{ minWidth: 150 }}
                />
              ) : filterType === 'state' && (currentFilter.field === 'name' || currentFilter.field === 'abbreviation') ? (
                <TextField
                  label="Value"
                  value={currentFilter.value}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
              ) : filterType === 'country' && (currentFilter.field === 'name' || currentFilter.field === 'abbreviation') ? (
                <TextField
                  label="Value"
                  value={currentFilter.value}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
              ) : filterType === 'industry_tag' && currentFilter.field === 'name' ? (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {allIndustryTagNames.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : filterType === 'industry_tag' && currentFilter.field === 'status' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="1">1 (Active)</MenuItem>
                    <MenuItem value="0">0 (Inactive)</MenuItem>
                  </Select>
                </FormControl>
              ) : filterType === 'industry_tag' && currentFilter.field === 'type_' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="1">1</MenuItem>
                    <MenuItem value="0">0</MenuItem>
                  </Select>
                </FormControl>
              ) : filterType === 'industry_tag' && currentFilter.field === 'observations' ? (
                <TextField
                  label="Value"
                  value={currentFilter.value}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
              ) : currentFilter.field === 'type_' && filterType === 'people' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="THC">THC</MenuItem>
                    <MenuItem value="CBD">CBD</MenuItem>
                    <MenuItem value="INV">INV</MenuItem>
                    <MenuItem value="MED">MED</MenuItem>
                    <MenuItem value="THC, CBD">THC, CBD</MenuItem>
                    <MenuItem value="THC, INV">THC, INV</MenuItem>
                    <MenuItem value="THC, MED">THC, MED</MenuItem>
                    <MenuItem value="CBD, INV">CBD, INV</MenuItem>
                    <MenuItem value="CBD, MED">CBD, MED</MenuItem>
                    <MenuItem value="INV, MED">INV, MED</MenuItem>
                    <MenuItem value="THC, CBD, INV">THC, CBD, INV</MenuItem>
                    <MenuItem value="THC, CBD, MED">THC, CBD, MED</MenuItem>
                    <MenuItem value="CBD, INV, MED">CBD, INV, MED</MenuItem>
                    <MenuItem value="THC, CBD, INV, MED">THC, CBD, INV, MED</MenuItem>
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'b2b_or_b2c' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="B2B">B2B</MenuItem>
                    <MenuItem value="B2C">B2C</MenuItem>
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'p_tag' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {industryTags.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'type_' && filterType === 'company' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="THC">THC</MenuItem>
                    <MenuItem value="CBD">CBD</MenuItem>
                    <MenuItem value="INV">INV</MenuItem>
                    <MenuItem value="MED">MED</MenuItem>
                    <MenuItem value="THC, CBD">THC, CBD</MenuItem>
                    <MenuItem value="THC, INV">THC, INV</MenuItem>
                    <MenuItem value="THC, MED">THC, MED</MenuItem>
                    <MenuItem value="CBD, INV">CBD, INV</MenuItem>
                    <MenuItem value="CBD, MED">CBD, MED</MenuItem>
                    <MenuItem value="INV, MED">INV, MED</MenuItem>
                    <MenuItem value="THC, CBD, INV">THC, CBD, INV</MenuItem>
                    <MenuItem value="THC, CBD, MED">THC, CBD, MED</MenuItem>
                    <MenuItem value="CBD, INV, MED">CBD, INV, MED</MenuItem>
                    <MenuItem value="THC, CBD, INV, MED">THC, CBD, INV, MED</MenuItem>
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'industry_tag' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {industryTags.map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'state_usa' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {states.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'country' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'verification_status' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {verificationStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : ['date_added', 'date_updated', 'last_emailed', 'last_clicked', 'last_opened', 'timestamp', 'last_ran_snov', 'last_ran_gmo', 'last_ran_reoon'].includes(currentFilter.field) ? (
                // Date field inputs
                currentFilter.operator === 'date_between' ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      label="From Date"
                      type="date"
                      value={currentFilter.value?.from || ''}
                      onChange={(e) => setCurrentFilter({ 
                        ...currentFilter, 
                        value: { ...(currentFilter.value || {}), from: e.target.value } 
                      })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                    <Typography>to</Typography>
                    <TextField
                      label="To Date"
                      type="date"
                      value={currentFilter.value?.to || ''}
                      onChange={(e) => setCurrentFilter({ 
                        ...currentFilter, 
                        value: { ...(currentFilter.value || {}), to: e.target.value } 
                      })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                  </Box>
                ) : (
                  <TextField
                    label={currentFilter.operator === 'date_from' ? 'From Date' : 
                           currentFilter.operator === 'date_to' ? 'To Date' : 
                           currentFilter.operator === 'date_exact' ? 'Exact Date' : 'Value'}
                    type="date"
                    value={currentFilter.value || ''}
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                )
              ) : currentFilter.field === 'username' && filterType === 'log' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value || ''}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    {usernames.map((email) => (
                      <MenuItem key={email} value={email}>
                        {email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'table_name' && filterType === 'log' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value || ''}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="record">record</MenuItem>
                    <MenuItem value="company">company</MenuItem>
                    <MenuItem value="pais">pais</MenuItem>
                    <MenuItem value="industry_tag">industry_tag</MenuItem>
                    <MenuItem value="estado">estado</MenuItem>
                  </Select>
                </FormControl>
              ) : currentFilter.field === 'operation' && filterType === 'log' ? (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={currentFilter.value || ''}
                    label="Value"
                    onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  >
                    <MenuItem value="CREATE">CREATE</MenuItem>
                    <MenuItem value="UPDATE">UPDATE</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                    <MenuItem value="IMPORT">IMPORT</MenuItem>
                    <MenuItem value="DOWNLOAD">DOWNLOAD</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <TextField
                  label="Value"
                  value={currentFilter.value}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, value: e.target.value })}
                  sx={{ minWidth: 150 }}
                />
              )
            )}
          {filters.length === 0 && (
            <Button
              variant="outlined"
              onClick={handleAddFilter}
              disabled={isAddFilterDisabled()}
              sx={{ alignSelf: 'flex-end' }}
            >
              Add Filter
            </Button>
          )}
          </Box>

          {/* Logic options only show when there are existing filters */}
          {filters.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl>
                <FormLabel component="legend">Logic</FormLabel>
                <RadioGroup
                  row
                  value={currentFilter.logic}
                  onChange={(e) => setCurrentFilter({ ...currentFilter, logic: e.target.value as 'AND' | 'OR' })}
                >
                  <FormControlLabel value="AND" control={<Radio />} label="AND" />
                  <FormControlLabel value="OR" control={<Radio />} label="OR" />
                </RadioGroup>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleAddFilter}
                disabled={isAddFilterDisabled()}
              >
                Add Filter
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearAllFilters} startIcon={<ClearIcon />}>
          Clear All
        </Button>
        <Button onClick={() => setCriteriaOpen(false)}>Cancel</Button>
        <Button onClick={handleApplyFilters} variant="contained">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: filters.length > 0 ? 1 : 0, width: '100%', alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0 }}>
        <Button
          variant="outlined"
          startIcon={<ViewColumnsIcon />}
          onClick={() => setViewColumnsOpen(true)}
        >
          View Columns
        </Button>
        
        {showFilters && (
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setCriteriaOpen(true)}
          >
            Filters
            {filters.length > 0 && (
              <Chip
                label={filters.length}
                size="small"
                sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Button>
        )}

        {onEdit && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
          >
            Edit
          </Button>
        )}

        {onImport && (
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={onImport}
          >
            Import
          </Button>
        )}

        {onDownload && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download
          </Button>
        )}

        {filters.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClearAllFilters}
            color="error"
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Inline Horizontal Query Row */}
      {filters.length > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5, 
            flexWrap: 'wrap', 
            mt: 0.5,
            pt: 0.5,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            width: '100%',
          }}
        >
          <Typography 
            variant="caption" 
            sx={{ 
              fontWeight: 600, 
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.7rem',
              mr: 0.5,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Active Queries:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {filters.map((filter, index) => {
              const fieldLabel = filterOptions.find(opt => opt.value === filter.field)?.label || filter.field;
              const currentOperatorOptions = getOperatorOptions(filter.field);
              const operatorLabel = currentOperatorOptions.find(opt => opt.value === filter.operator)?.label || filter.operator;
              let valueLabel = '';
              
              if (['is_null', 'is_not_null'].includes(filter.operator)) {
                valueLabel = '';
              } else if (filter.operator === 'date_between' && filter.value) {
                valueLabel = ` "${filter.value.from}" to "${filter.value.to}"`;
              } else {
                valueLabel = ` "${filter.value}"`;
              }
              
              return (
                <Chip
                  key={index}
                  label={`${index > 0 ? `(${filter.logic}) ` : ''}${fieldLabel} ${operatorLabel}${valueLabel}`}
                  onDelete={() => handleRemoveFilterInline(index)}
                  size="small"
                  sx={{ 
                    height: 24, 
                    fontSize: '0.75rem',
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: 'primary.main',
                    fontWeight: 500,
                    border: '1px solid rgba(25, 118, 210, 0.15)',
                    '& .MuiChip-deleteIcon': {
                      color: 'primary.main',
                      opacity: 0.7,
                      '&:hover': {
                        color: 'primary.dark',
                        opacity: 1,
                      }
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {renderViewColumnsDialog()}
      {renderCriteriaDialog()}
    </Box>
  );
};

export default CompactFilterPanel;
